import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useStatusCustomizados, useCriarStatus, useAtualizarStatus } from "@/hooks/useStatusCustomizados";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Palette } from "lucide-react";

type SetorType = 'projeto' | 'orcamento' | 'pedido' | 'contrato' | 'financeiro' | 'sinistro' | 'servico';

const setorLabels: Record<SetorType, string> = {
  projeto: 'Projeto',
  orcamento: 'Orçamento', 
  pedido: 'Pedido',
  contrato: 'Contrato',
  financeiro: 'Financeiro',
  sinistro: 'Sinistro',
  servico: 'Serviço'
};

const coresDisponiveis = [
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#f59e0b', label: 'Laranja' },
  { value: '#eab308', label: 'Amarelo' },
  { value: '#10b981', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#6b7280', label: 'Cinza' },
  { value: '#ec4899', label: 'Rosa' }
];

export const StatusCustomizados = () => {
  const { data: statusList, isLoading } = useStatusCustomizados();
  const criarStatus = useCriarStatus();
  const atualizarStatus = useAtualizarStatus();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<any>(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    setor: '' as SetorType | '',
    cor: '#6b7280',
    ordem: '0'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.setor) {
      toast({ title: "Selecione um setor", variant: "destructive" });
      return;
    }
    
    try {
      const statusData = {
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        setor: formData.setor as SetorType,
        cor: formData.cor,
        ordem: parseInt(formData.ordem)
      };

      if (editingStatus) {
        await atualizarStatus.mutateAsync({ id: editingStatus.id, ...statusData });
        toast({ title: "Status atualizado com sucesso!" });
      } else {
        await criarStatus.mutateAsync(statusData);
        toast({ title: "Status criado com sucesso!" });
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({ 
        title: "Erro ao salvar status", 
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive" 
      });
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descricao: '',
      setor: '' as SetorType | '',
      cor: '#6b7280',
      ordem: '0'
    });
    setEditingStatus(null);
  };

  const handleEdit = (status: any) => {
    setEditingStatus(status);
    setFormData({
      nome: status.nome,
      descricao: status.descricao || '',
      setor: status.setor,
      cor: status.cor || '#6b7280',
      ordem: status.ordem?.toString() || '0'
    });
    setDialogOpen(true);
  };

  const groupedStatus = statusList?.reduce((acc, status) => {
    if (!acc[status.setor]) {
      acc[status.setor] = [];
    }
    acc[status.setor].push(status);
    return acc;
  }, {} as Record<string, any[]>);

  if (isLoading) {
    return <div className="p-6">Carregando status...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Status Customizados</h1>
          <p className="text-gray-500">Configure status personalizados para cada setor do sistema</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Status
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingStatus ? 'Editar Status' : 'Novo Status'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome do Status</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Pagamento Confirmado"
                  required
                />
              </div>

              <div>
                <Label htmlFor="setor">Setor</Label>
                <Select value={formData.setor} onValueChange={(value) => setFormData(prev => ({ ...prev, setor: value as SetorType }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(setorLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição opcional do status"
                />
              </div>

              <div>
                <Label htmlFor="cor">Cor</Label>
                <div className="flex gap-2 mt-2">
                  {coresDisponiveis.map((cor) => (
                    <button
                      key={cor.value}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 ${formData.cor === cor.value ? 'border-gray-900' : 'border-gray-300'}`}
                      style={{ backgroundColor: cor.value }}
                      onClick={() => setFormData(prev => ({ ...prev, cor: cor.value }))}
                      title={cor.label}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="ordem">Ordem de Exibição</Label>
                <Input
                  id="ordem"
                  type="number"
                  value={formData.ordem}
                  onChange={(e) => setFormData(prev => ({ ...prev, ordem: e.target.value }))}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={criarStatus.isPending || atualizarStatus.isPending}>
                  {editingStatus ? 'Atualizar' : 'Criar'} Status
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-8">
        {Object.entries(setorLabels).map(([setor, label]) => {
          const statusDoSetor = groupedStatus?.[setor] || [];
          
          return (
            <Card key={setor}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  {label}
                  <Badge variant="secondary">{statusDoSetor.length} status</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statusDoSetor.length === 0 ? (
                  <p className="text-gray-500 text-sm">Nenhum status configurado para este setor</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {statusDoSetor
                      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                      .map((status) => (
                        <div
                          key={status.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: status.cor }}
                            />
                            <div>
                              <p className="font-medium">{status.nome}</p>
                              {status.descricao && (
                                <p className="text-xs text-gray-500">{status.descricao}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">#{status.ordem}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(status)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};